import { css } from 'aphrodite';
import * as React from 'react';
import { type CoachingAdherenceRepScore, type Tier, formatPercent, shareTier } from '../../constants/coachingAdherence';
import {
	COMPLIANT_SHARE,
	type CoachStage,
	type ScoreboardSummary,
	formatPerDemo,
	formatRate,
	isAutoMode,
	summarizeStageUse,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';

const TIER_ACCENTS = {
	good: styleSheet.tileGood,
	warn: styleSheet.tileWarn,
	bad: styleSheet.tileBad,
	neutral: styleSheet.tileNeutral,
} as const satisfies Record<Tier, object>;

function StatTile({
	label,
	value,
	detail,
	tier = 'neutral',
}: {
	label: string;
	value: string;
	detail?: React.ReactNode;
	tier?: Tier;
}) {
	return (
		<div role='group' aria-label={label} className={css(styleSheet.tile, TIER_ACCENTS[tier])}>
			<span className={css(styleSheet.tileLabel)}>{label}</span>
			<span className={css(styleSheet.tileValue)}>{value}</span>
			{detail ? <span className={css(styleSheet.tileDetail)}>{detail}</span> : null}
		</div>
	);
}

const STAGE_TILES: Array<[CoachStage, string, string]> = [
	['hook', 'Hook used', 'of calls with a coached hook to take'],
	['opener', 'Opener used', 'of calls with a researched opener to use'],
	['ask', 'Ask made', 'of calls with the coach’s meeting ask to make'],
];

const count = (value: number) => value.toLocaleString();

interface IProps {
	summary: ScoreboardSummary;
	reps: CoachingAdherenceRepScore[];
	/** The one rep in view, when a rep is picked: tiles describe them rather than a group. */
	selectedRep: CoachingAdherenceRepScore | null;
	followedScore: number;
}

/** The board's dashboard: how the reps in view used the coach, then how they dialed and booked. */
export function ScoreboardSummaryTiles({ summary, reps, selectedRep, followedScore }: IProps) {
	const stageUse = summarizeStageUse(reps);
	const { activity } = summary;

	return (
		<div className={css(styleSheet.dashboard)}>
			<section aria-label='Coach use' className={css(styleSheet.tileSection)}>
				<h3 className={css(styleSheet.tileSectionTitle)}>Coach use</h3>
				<div className={css(styleSheet.tileGrid)}>
					<StatTile
						label='Followed the coach'
						value={formatPercent(summary.cohortShare)}
						tier={shareTier(summary.cohortShare)}
						detail={`of ${count(summary.pastHookCalls)} calls past the hook scored ${followedScore}+`}
					/>
					{summary.partlyShare != null ? (
						<StatTile
							label='Used it at least partly'
							value={formatPercent(summary.partlyShare)}
							tier='warn'
							detail={`scored ${followedScore / 2}+`}
						/>
					) : null}
					{selectedRep ? (
						<StatTile
							label='Calls followed'
							value={`${selectedRep.followedCalls} of ${selectedRep.pastHookCalls}`}
							detail='calls past the hook'
						/>
					) : (
						<StatTile
							label='Reps compliant'
							value={`${summary.compliantReps} of ${summary.rankedReps}`}
							detail={`scored ${followedScore / 2}+ on ${formatPercent(COMPLIANT_SHARE)} or more of their calls; ${summary.neverFollowed} never did`}
						/>
					)}
					{summary.autoReps != null && selectedRep ? (
						<StatTile
							label='Coach mode'
							value={isAutoMode(selectedRep) ? 'Auto' : 'Get suggestion'}
							detail={isAutoMode(selectedRep) ? 'cards show on their own' : 'cards show only when asked'}
						/>
					) : null}
					{STAGE_TILES.filter(([stage]) => stageUse[stage] != null).map(([stage, label, detail]) => (
						<StatTile
							key={stage}
							label={label}
							value={formatPercent(stageUse[stage])}
							tier={shareTier(stageUse[stage])}
							detail={detail}
						/>
					))}
				</div>
			</section>
			<section aria-label='Results' className={css(styleSheet.tileSection)}>
				<h3 className={css(styleSheet.tileSectionTitle)}>Results</h3>
				<div className={css(styleSheet.tileGrid)}>
					{activity ? (
						<>
							<StatTile
								label='Dials'
								value={count(activity.allDials)}
								detail={`${count(activity.dials)} prospecting · ${count(activity.allDials - activity.dials)} confirm or reschedule`}
							/>
							<StatTile
								label='Dials per demo'
								value={formatPerDemo(activity.dialsPerDemo)}
								detail={`${count(activity.dials)} prospecting dials · ${count(activity.demos)} demos`}
							/>
							<StatTile
								label='Connects per demo'
								value={formatPerDemo(activity.connectsPerDemo)}
								detail={`${count(activity.connects)} connects · ${count(activity.demos)} demos`}
							/>
						</>
					) : null}
					<StatTile
						label='Demos per call past the hook'
						value={formatRate(summary.demosPerPastHookCall)}
						detail={`${formatRate(summary.demosPerFollowedCall)} when the rep followed the coach`}
					/>
				</div>
			</section>
		</div>
	);
}
